"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Shield, Star } from "lucide-react";

type Comment = {
  id: string;
  text: string;
  createdAt: string;
  user: {
    id: string;
    username: string;
    avatarUrl: string | null;
    role: string;
    isVerified: boolean;
    isPremium: boolean;
  };
};

const POLL_INTERVAL = 3000;

export default function ContentChat({ contentId }: { contentId: string }) {
  const [messages, setMessages] = useState<Comment[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMsgRef = useRef<string | null>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const url = lastMsgRef.current
        ? `/api/content/${contentId}/chat?after=${lastMsgRef.current}`
        : `/api/content/${contentId}/chat`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data: Comment[] = await res.json();
      if (data.length > 0) {
        lastMsgRef.current = data[data.length - 1].id;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...data.filter((m) => !ids.has(m.id))];
        });
      }
    } catch {}
  }, [contentId]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/api/content/${contentId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages((prev) => [...prev, msg]);
        lastMsgRef.current = msg.id;
        setInput("");
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.error) alert(err.error);
      }
    } catch {} finally { setSending(false); }
  };

  const getRoleColor = (role: string, isPremium: boolean) => {
    if (role === "ADMIN") return "text-red-500";
    if (role === "CREATOR") return "text-blue-400";
    if (isPremium) return "text-yellow-500";
    return "text-neutral-400";
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0a0a] border border-neutral-800/60 rounded-xl overflow-hidden">
      <div className="h-10 bg-[#0e0e0e] border-b border-neutral-800/60 flex items-center justify-between px-4 shrink-0">
        <span className="text-[11px] font-bold text-neutral-300 uppercase tracking-wider">Kites</span>
        <span className="text-[10px] font-mono text-neutral-600">{messages.length}</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-hide">
        {messages.length === 0 && (
          <div className="text-center text-neutral-600 text-xs py-8 font-mono uppercase tracking-wider">
            No kites yet
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="flex gap-2 text-[13px] animate-fade-in">
            <div className="shrink-0 mt-0.5">
              {msg.user.avatarUrl ? (
                <img src={msg.user.avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover border border-neutral-800" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-neutral-800 flex items-center justify-center text-[8px] font-bold text-neutral-500">
                  {msg.user.username[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className={`text-[11px] font-bold mr-1.5 ${getRoleColor(msg.user.role, msg.user.isPremium)}`}>
                {msg.user.username}
                {msg.user.role === "CREATOR" && <Shield className="w-2.5 h-2.5 inline ml-0.5 text-blue-500" />}
                {msg.user.isPremium && msg.user.role !== "CREATOR" && <Star className="w-2.5 h-2.5 inline ml-0.5 text-yellow-600" />}
              </span>
              <span className="text-neutral-300 break-words">{msg.text}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="p-2.5 bg-[#0e0e0e] border-t border-neutral-800/60 shrink-0">
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Send a kite..."
            className="flex-1 h-8 bg-neutral-900 border border-neutral-800 rounded-md pl-3 pr-3 text-[13px] text-white placeholder-neutral-600 focus:border-red-700 focus:outline-none transition"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-red-600 hover:bg-red-500 text-white transition disabled:opacity-30 shrink-0"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
